import React, { useState, useEffect, useRef } from 'react';
import { Box, Flex, Text, Divider, Badge, Button, Spinner, useColorModeValue } from '@chakra-ui/react';
import { Droppable } from 'react-beautiful-dnd';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import NewKBLeadCard from './NewKBLeadCard';
import { useNewKBLead } from '../context/NewKBLeadContext';
import { STAGE_IDS } from '../utils/NewKBConstants';

const NewKBColumn = ({ stage, dragDisabled = false, filteredLeads = null }) => {
  const { 
    expandedStages, 
    toggleStageExpansion, 
    getLeadsByStage, 
    leads: allLeads,
    loadClosedLeads,
    closedLeadsLoaded,
    isLoadingClosedLeads
  } = useNewKBLead();
  
  // Add state to track if component is mounted for react-beautiful-dnd stability
  const [isMounted, setIsMounted] = useState(false);
  // Track the leads for this stage in local state
  const [stageLeads, setStageLeads] = useState([]);
  
  // Add refs to track scroll position
  const columnRef = useRef(null);
  const scrollPosition = useRef(0);

  // Refresh leads when allLeads changes or when filteredLeads is provided
  useEffect(() => {
    // If filteredLeads prop is provided, use it directly
    if (filteredLeads !== null) {
      console.log(`[NewKBColumn] Using filtered leads for stage ${stage.name}:`, filteredLeads.length);
      setStageLeads(filteredLeads);
    } else {
      // Otherwise, use the context method to get leads for this stage
      const contextLeads = getLeadsByStage(stage._id);
      console.log(`[NewKBColumn] Using context leads for stage ${stage.name}:`, contextLeads.length);
      setStageLeads(contextLeads);
    }
  }, [allLeads, stage._id, getLeadsByStage, filteredLeads, stage.name]);

  useEffect(() => {
    // Set mounted state after a small delay to ensure all contexts are ready
    const timer = setTimeout(() => {
      setIsMounted(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  
  // Save scroll position before updating leads
  useEffect(() => {
    // This function captures the current column ref
    const currentColumnRef = columnRef.current;
    
    if (currentColumnRef) {
      return () => {
        try {
          // Only access scrollTop if the ref is still valid
          if (columnRef.current) {
            scrollPosition.current = columnRef.current.scrollTop;
            console.log(`[NewKBColumn] Saved scroll position for ${stage.name}:`, scrollPosition.current);
          } else {
            console.log(`[NewKBColumn] Column ref was null during cleanup for ${stage.name}`);
          }
        } catch (error) {
          console.error(`[NewKBColumn] Error saving scroll position for ${stage.name}:`, error);
        }
      };
    }
  }, [stageLeads, stage.name]);

  // Restore scroll position after render
  useEffect(() => {
    if (columnRef.current && scrollPosition.current > 0) {
      setTimeout(() => {
        columnRef.current.scrollTop = scrollPosition.current;
        console.log(`[NewKBColumn] Restored scroll position for ${stage.name}:`, scrollPosition.current);
      }, 0);
    }
  }, [stageLeads, stage.name]);
  
  const isExpanded = expandedStages[stage._id];
  // Use our local state which updates when context changes
  const leads = stageLeads;
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  // Stage is closed if it contains "closed" in ID
  const isClosed = stage._id.includes('closed');

  return (
    <Box
      minW={!isExpanded ? "50px" : "320px"}
      maxW={!isExpanded ? "50px" : "320px"}
      mx={2}
      borderWidth="1px"
      borderRadius="md"
      bg={bgColor}
      borderColor={borderColor}
      boxShadow="sm"
      height={isClosed ? "fit-content" : "calc(100vh-40px)"}
      display="flex"
      flexDirection="column"
      flexShrink={0}
      transition="all 0.3s ease"
    >
      {/* Column Header */}
      <Flex
        p={2}
        align="center"
        justify="space-between"
        onClick={() => toggleStageExpansion(stage._id)}
        cursor="pointer"
        bg={useColorModeValue('gray.50', 'gray.600')}
        borderTopRadius="md"
        flexDirection={!isExpanded ? "column" : "row"}
      >
        <Flex align="center" justifyContent={!isExpanded ? "center" : "flex-start"} width="100%">
          {!isExpanded ? (
            <Box 
              height="200px" 
              width="100%"
              position="relative"
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Badge 
                position="absolute" 
                top="10px" 
                colorScheme="blue" 
                borderRadius="full" 
                fontSize="xs"
              >
                {leads.length}
              </Badge>
              <Flex 
                transform="rotate(-90deg)" 
                position="absolute"
                direction="column" 
                align="center"
              >
                <Text
                  fontWeight="bold" 
                  fontSize="sm"
                  letterSpacing="tight"
                  whiteSpace="nowrap"
                  color="gray.700"
                >
                  {stage.name.split(' ')[0]}
                </Text>
                {stage.name.split(' ').length > 1 && (
                  <Text
                    fontWeight="bold" 
                    fontSize="sm"
                    letterSpacing="tight"
                    whiteSpace="nowrap"
                    color="gray.700"
                    mt="-2px"
                  >
                    {stage.name.split(' ')[1]}
                  </Text>
                )}
              </Flex>
            </Box>
          ) : (
            <>
              <Text fontWeight="bold">{stage.name}</Text>
              <Badge ml={2} colorScheme="blue" borderRadius="full">
                {leads.length}
              </Badge>
            </>
          )}
        </Flex>
        {isExpanded && <ChevronUpIcon boxSize={5} />}
      </Flex>
      
      <Divider />
      
      {/* Column Content */}
      {isMounted ? (
        <Droppable droppableId={stage._id} type="LEAD">
          {(provided) => (
            <Box
              ref={(el) => {
                // Combine the droppable ref with our scroll tracking ref
                provided.innerRef(el);
                columnRef.current = el;
              }}
              {...provided.droppableProps}
              flex="1"
              minH="50px"
              overflowY="auto"
              p={1}
              display={isExpanded ? "block" : "none"} // Hide content but keep droppable mounted
              data-stage-id={stage._id} // Add data attribute for debugging
              sx={{
                '&::-webkit-scrollbar': {
                  width: '6px',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  borderRadius: '4px',
                },
              }}
            >
              {/* Show Load Closed Leads button for closed_lost stage when not loaded */}
              {stage._id === 'stage_closed_lost' && !closedLeadsLoaded && (
                <Box textAlign="center" py={4}>
                  <Button
                    size="sm"
                    colorScheme="gray"
                    onClick={(e) => {
                      e.stopPropagation();
                      loadClosedLeads();
                    }}
                    isLoading={isLoadingClosedLeads}
                    loadingText="Loading..."
                  >
                    {isLoadingClosedLeads ? <Spinner size="sm" /> : 'Load Closed Leads'}
                  </Button>
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    Closed leads are loaded on demand
                  </Text>
                </Box>
              )}
              {leads.map((lead, index) => {
                // Create a lead update handler
                const handleLeadUpdate = (updatedLead) => {
                  console.log('[NewKBColumn] Lead updated callback:', {
                    leadId: updatedLead.leadId,
                    name: updatedLead.brideAndGroomName,
                    stage: updatedLead.stage,
                    timestamp: new Date().toLocaleTimeString()
                  });
                  
                  // Force an immediate update to our local state
                  setStageLeads(prevLeads => {
                    // Find and replace the updated lead
                    const leadIndex = prevLeads.findIndex(l => 
                      (l.leadId && l.leadId === updatedLead.leadId) ||
                      (l._id && l._id === updatedLead._id)
                    );
                    
                    if (leadIndex !== -1) {
                      console.log(`[NewKBColumn] Directly updating lead at index ${leadIndex}`);
                      const newLeads = [...prevLeads];
                      // Create a new reference to ensure React detects the change
                      newLeads[leadIndex] = {
                        ...JSON.parse(JSON.stringify(updatedLead)), // Deep clone to break all references
                        _localUpdateTime: Date.now() // Add timestamp to force re-rendering
                      };
                      return newLeads;
                    }
                    return prevLeads;
                  });
                };
                
                // Ensure we have a valid business leadId for dragging
                const hasValidId = Boolean(lead.leadId && lead.leadId.startsWith('SUM-'));
                const leadKey = lead.leadId || `lead-${lead._id || index}`;
                
                // Debug ID information
                if (!hasValidId) {
                  console.log(`[NewKBColumn] Lead missing valid leadId:`, {
                    _id: lead._id,
                    leadId: lead.leadId,
                    index
                  });
                }
                
                return (
                  <NewKBLeadCard
                    key={leadKey}
                    lead={lead}
                    index={index}
                    onLeadUpdate={handleLeadUpdate}
                    isDragDisabled={dragDisabled || !hasValidId}
                  />
                );
              })}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      ) : (
        // Placeholder while mounting to prevent errors
        <Box
          minH="100px"
          maxH={isClosed ? "300px" : "calc(100vh - 250px)"}
          overflowY="auto"
          p={2}
          display={isExpanded ? "block" : "none"}
        />
      )}
    </Box>
  );
};

export default NewKBColumn;
